import config from '@/config';

type MatomoCommand =
    | ['trackPageView']
    | ['trackEvent', string, string, string?, number?]
    | ['setUserId', string]
    | ['resetUserId']
    | ['setCustomDimension', number, string]
    | ['deleteCustomDimension', number]
    | ['setCustomVariable', number, string, string, 'visit' | 'page']
    | ['deleteCustomVariable', number, 'visit' | 'page']
    | ['enableLinkTracking']
    | ['setTrackerUrl', string]
    | ['setSiteId', string | number]
    | ['setCookieSameSite', string]
    | ['setCustomUrl', string];

declare global {
    interface Window {
        _paq?: MatomoCommand[] | { push: (command: MatomoCommand) => void };
    }
}

/**
 * Checks if Matomo is available and enabled.
 *
 * @returns {boolean} Whether Matomo is available and enabled.
 */
const isMatomoAvailable = (): boolean => {
    return config.matomo.enabled && window?._paq?.push instanceof Function;
};

/**
 * Pushes a command to Matomo _paq queue.
 *
 * @param {MatomoCommand} command - Matomo command to push
 * @returns {void}
 */
const pushCommand = (command: MatomoCommand): void => {
    if (isMatomoAvailable()) {
        window._paq!.push(command);
    }
};

/**
 * Removes sensitive query parameters from a URL.
 *
 * @param {string} url - URL to sanitize
 * @returns {string} Sanitized URL without sensitive parameters
 */
const sanitizeUrl = (url: string): string => {
    try {
        const urlObj = new URL(url, window.location.origin);
        const sensitiveParams = ['jwt'];

        sensitiveParams.forEach(param => {
            urlObj.searchParams.delete(param);
        });

        return urlObj.pathname + urlObj.search + urlObj.hash;
    } catch {
        return url;
    }
};

/**
 * Tracks a page view. This is called automatically by Matomo script on initial load.
 * The URL is sanitized to remove sensitive query parameters like JWT tokens.
 *
 * @returns {void}
 */
export const trackPageView = (): void => {
    const sanitizedUrl = sanitizeUrl(window.location.href);

    pushCommand(['setCustomUrl', sanitizedUrl]);
    pushCommand(['trackPageView']);
};

/**
 * Sends a Matomo event for analytics.
 *
 * @param {string} category - Organize tracked events into groups
 * @param {string} action - Informs what a user did. For example, clicked, played, shared, and more.
 * @param {string} [name] - Optional description of the element being tracking, like the call-to-action on the button.
 * @param {number} [value] - An optional numerical value which can be added to the event (shown as cumulative value on an event report)
 * @returns {void}
 */
export const pushTrackEvent = (category: string, action: string, name?: string, value?: number): void => {
    const command: MatomoCommand = ['trackEvent', category, action, name, value] as MatomoCommand;

    pushCommand(command);
};
